import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  mlaDate,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  fullDate,
  get,
  italic,
  type LegalValues,
  mainTitle,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * Treaties and international conventions in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7 §11.10, "Treaties and International Conventions" (Publication Manual p. 368),
 *   as reproduced by Liberty University and Columbus State: "Name of Treaty or
 *   Convention, Month Day, Year, URL" — "United Nations Convention on the Rights of the
 *   Child, November 20, 1989, https://www.ohchr.org/…". The reference needs only the
 *   name, the signing or approval date and a URL if available. In text: (Name, Year).
 * - Bluebook 22d ed. rule 21.4 (treaties): name, parties if bilateral (abbreviated and
 *   hyphenated, "U.S.-Japan"), subdivision, date of signing, and a treaty series —
 *   "Convention on the Rights of the Child, Nov. 20, 1989, 1577 U.N.T.S. 3."
 * - Chicago 18 §14.191 (treaties): Chicago's legal-citation advice relies on the
 *   Bluebook (§14.170; CMOS Q&A on laws and statutes), so the note keeps the Bluebook
 *   order, with the month spelled out as in Chicago's other notes. Legal and public
 *   documents go in notes, not the bibliography (Lone Star and Portland CMOS 18 guides).
 * - MLA 9: MLA Style Center, "Documenting Legal Works" — "Kyoto Protocol to the United
 *   Nations Framework Convention on Climate Change. United Nations, 1998, …kpeng.pdf.
 *   Multilateral treaty." Treaty titles are roman; the source's own date follows the
 *   title, as in the Style Center's constitution and resolution examples.
 * - Treaty facts for the page example: UN Treaty Collection, chapter IV.11 (Convention
 *   on the Rights of the Child, New York, 20 November 1989; United Nations, Treaty
 *   Series, vol. 1577, p. 3).
 */

export const TREATY_FIELDS = {
  name: "name",
  shortTitle: "shortTitle",
  kind: "kind",
  parties: "parties",
  date: "date",
  article: "article",
  series: "series",
  seriesVolume: "seriesVolume",
  seriesPage: "seriesPage",
  website: "website",
  url: "url",
} as const;

const F = TREATY_FIELDS;

export type TreatySeries =
  | "none"
  | "unts"
  | "ust"
  | "tias"
  | "ilm"
  | "stat"
  | "lnts";

/** Bluebook abbreviations for the treaty series. T.I.A.S. is cited by number alone. */
const SERIES_ABBREVIATIONS: Readonly<
  Record<Exclude<TreatySeries, "none">, string>
> = {
  unts: "U.N.T.S.",
  ust: "U.S.T.",
  tias: "T.I.A.S. No.",
  ilm: "I.L.M.",
  stat: "Stat.",
  lnts: "L.N.T.S.",
};

export interface TreatyLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

const RE_ARTICLE_PREFIX = /^(arts?\.?|articles?)\s*/i;

/** "1577 U.N.T.S. 3", "T.I.A.S. No. 16-1104" — empty when no series is chosen. */
export function treatySeriesCite(values: LegalValues): string {
  const series = (get(values, F.series) || "none") as TreatySeries;
  if (series === "none") {
    return "";
  }
  const page = get(values, F.seriesPage);
  if (series === "tias") {
    return page ? `T.I.A.S. No. ${page}` : "";
  }
  const volume = get(values, F.seriesVolume);
  if (volume === "" || page === "") {
    return "";
  }
  return `${volume} ${SERIES_ABBREVIATIONS[series]} ${page}`;
}

/** Chicago, after Bluebook rule 21.4: name, parties, article, date, series, URL. */
function chicagoNote(values: LegalValues): string {
  const name = get(values, F.name);
  const bilateral = get(values, F.kind) === "bilateral";
  const parties = bilateral ? get(values, F.parties) : "";
  const article = get(values, F.article).replace(RE_ARTICLE_PREFIX, "");
  let head = name;
  if (parties) {
    head += `, ${parties}`;
  }
  if (article) {
    head += parties ? `, art. ${article}` : ` art. ${article}`;
  }
  const pieces = [
    head,
    fullDate(parsePartialDate(get(values, F.date))),
    treatySeriesCite(values),
    get(values, F.url),
  ].filter(Boolean);
  return `${pieces.join(", ")}.`;
}

export function treatyIsReady(values: LegalValues): boolean {
  return (
    get(values, F.name) !== "" && parsePartialDate(get(values, F.date)) !== null
  );
}

export function formatTreaty(
  values: LegalValues,
  labels: TreatyLabels
): CitationOutput[] {
  const name = get(values, F.name);
  const short = get(values, F.shortTitle) || mainTitle(name);
  const date = parsePartialDate(get(values, F.date));
  const year = date ? String(date.year) : "n.d.";
  const url = get(values, F.url);
  const website = get(values, F.website);
  const bilateral = get(values, F.kind) === "bilateral";

  const apaReference = url
    ? `${name}, ${fullDate(date)}, ${url}`
    : `${name}, ${fullDate(date)}.`;

  const mla: CitationParts = [
    plain(`${name}${closer(name)} `),
    plain(`${mlaDate(date)}. `),
  ];
  if (website) {
    mla.push(italic(website));
    mla.push(plain(url ? `, ${mlaUrl(url)}. ` : ". "));
  } else if (url) {
    mla.push(plain(`${mlaUrl(url)}. `));
  }
  mla.push(plain(bilateral ? "Bilateral treaty." : "Multilateral treaty."));

  return [
    {
      key: "apa",
      label: labels.apa,
      entries: [
        { label: labels.referenceList, parts: [plain(apaReference)] },
        { label: labels.parenthetical, parts: [plain(`(${short}, ${year})`)] },
        { label: labels.narrative, parts: [plain(`${short} (${year})`)] },
      ],
      note: labels.apaNote,
    },
    {
      key: "mla",
      label: labels.mla,
      entries: [
        { label: labels.worksCited, parts: tidyParts(mla) },
        { label: labels.inText, parts: [plain(`(${short})`)] },
      ],
      note: labels.mlaNote,
    },
    {
      key: "chicago",
      label: labels.chicago,
      entries: [
        { label: labels.footnote, parts: [plain(chicagoNote(values))] },
      ],
      note: labels.chicagoNote,
    },
  ];
}
